import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import {
  CampaignsListParams,
  ChangeCampaignStatusRequest,
  CreateCampaignRequest,
  UpdateCampaignRequest
} from '@donation/campaigns/models/campaigns.request';
import { ApiCampaignResponse as CampaignResponse } from '@donation/campaigns/models/campaigns.response';
import {
  ChangeContainerStatusRequest,
  ContainersListParams,
  CreateContainerRequest,
  UpdateContainerRequest
} from '@donation/containers/models/containers.request';
import { ApiContainerResponse as ContainerResponse } from '@donation/containers/models/containers.response';
import {
  AssociateDonationRequest,
  ChangeDonationStatusRequest,
  CreateDonationRequest,
  DonationsListParams,
  IssueDonationRefundRequest,
  UpdateDonationRequest
} from '@donation/donations/models/donations.request';
import { DonationRefundResponse, DonationResponse } from '@donation/donations/models/donations.response';
import {
  CreateDonorRequest,
  DonorsListParams,
  UpdateDonorRequest
} from '@donation/donors/models/donor-management.request';
import {
  DonorLoyaltyResponse,
  ApiDonorResponse as DonorResponse
} from '@donation/donors/models/donor-management.response';
import { ChangePresortStatusRequest, PresortQueueParams } from '@donation/presort/models/presort.request';
import { ApiPresortQueueResponse as PresortQueueResponse } from '@donation/presort/models/presort.response';
import {
  AvailableTimeSlotsParams,
  ChangeRewardStatusRequest,
  ChangeRewardTransactionStatusRequest,
  CreateAppConfigRequest,
  CreateLoyaltyTierRequest,
  CreatePaymentTerminalRequest,
  CreateRewardRequest,
  CreateStoreDonationTimeSlotRequest,
  PaymentTerminalListParams,
  RedeemRewardRequest,
  RewardTransactionsListParams,
  RewardsListParams,
  TimeSlotsListParams,
  UpdateAppConfigRequest,
  UpdateLoyaltyTierConfigRequest,
  UpdatePaymentTerminalRequest,
  UpdateRewardRequest,
  UpdateStoreDonationTimeSlotRequest
} from '@donation/settings/models/settings.request';
import {
  ApiPaymentTerminalResponse,
  ApiAppConfigResponse as AppConfigResponse,
  ConfigValidationResponse,
  LoyaltyTierConfigResponse,
  ApiRewardDefinitionResponse as RewardDefinitionResponse,
  ApiRewardTransactionResponse as RewardTransactionResponse,
  StoreDonationTimeSlotResponse
} from '@donation/settings/models/settings.response';
import { environment } from '@env/environment';
import { Observable, catchError, throwError } from 'rxjs';

import { PaginatedResult } from '../models/api.enum';
import { DonorTier } from '../models/domain.enum';
import { SharedStore } from '../models/domain.models';

/**
 * Thin typed wrapper around the donation API.
 * NO mapping logic — call mappers in feature services to convert to state models.
 */
@Injectable({ providedIn: 'root' })
export class DonationApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.donationApiUrl;

  // ─── Stores ──────────────────────────────────────────────────────────────

  getSharedStores(params?: { IsAcceptDonation?: boolean }): Observable<PaginatedResult<SharedStore>> {
    const p = this.toParams(params ?? {});
    return this.http.get<PaginatedResult<SharedStore>>(`${this.base}/SharedStores`, { params: p });
  }

  getStoreConfig(storeId: number): Observable<AppConfigResponse> {
    return this.http.get<AppConfigResponse>(`${this.base}/Stores/${storeId}/Config`);
  }

  updateStoreConfig(storeId: number, cmd: UpdateAppConfigRequest): Observable<AppConfigResponse> {
    return this.http.put<AppConfigResponse>(`${this.base}/Stores/${storeId}/Config`, cmd);
  }

  // ─── Store Donation Time Slots ────────────────────────────────────────────

  getAvailableTimeSlots(params: AvailableTimeSlotsParams): Observable<StoreDonationTimeSlotResponse[]> {
    const p = this.toParams(params);
    return this.http.get<StoreDonationTimeSlotResponse[]>(`${this.base}/StoreDonationTimeSlots/Available`, {
      params: p
    });
  }

  getTimeSlots(params: TimeSlotsListParams): Observable<PaginatedResult<StoreDonationTimeSlotResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<StoreDonationTimeSlotResponse>>(`${this.base}/StoreDonationTimeSlots`, {
      params: p
    });
  }

  createTimeSlot(cmd: CreateStoreDonationTimeSlotRequest): Observable<StoreDonationTimeSlotResponse> {
    return this.http.post<StoreDonationTimeSlotResponse>(`${this.base}/StoreDonationTimeSlots`, cmd);
  }

  updateTimeSlot(id: number, cmd: UpdateStoreDonationTimeSlotRequest): Observable<StoreDonationTimeSlotResponse> {
    return this.http.put<StoreDonationTimeSlotResponse>(`${this.base}/StoreDonationTimeSlots/${id}`, cmd);
  }

  // ─── Rewards ──────────────────────────────────────────────────────────────

  getRewards(params: RewardsListParams): Observable<PaginatedResult<RewardDefinitionResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<RewardDefinitionResponse>>(`${this.base}/Rewards`, { params: p });
  }

  createReward(cmd: CreateRewardRequest): Observable<RewardDefinitionResponse> {
    return this.http.post<RewardDefinitionResponse>(`${this.base}/Rewards`, cmd);
  }

  updateReward(id: number, cmd: UpdateRewardRequest): Observable<RewardDefinitionResponse> {
    return this.http.put<RewardDefinitionResponse>(`${this.base}/Rewards/${id}`, cmd);
  }

  deleteReward(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.base}/Rewards/${id}`);
  }

  changeRewardStatus(id: number, cmd: ChangeRewardStatusRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Rewards/${id}/ChangeStatus`, cmd);
  }

  getRewardTransactions(params: RewardTransactionsListParams): Observable<PaginatedResult<RewardTransactionResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<RewardTransactionResponse>>(`${this.base}/Rewards/Transactions`, {
      params: p
    });
  }

  redeemReward(cmd: RedeemRewardRequest): Observable<RewardTransactionResponse> {
    return this.http.post<RewardTransactionResponse>(`${this.base}/Rewards/Transactions`, cmd);
  }

  changeRewardTransactionStatus(id: number, cmd: ChangeRewardTransactionStatusRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Rewards/Transactions/${id}/ChangeStatus`, cmd);
  }

  // ─── Departments & Categories ─────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
  getDepartments(companyId: number): Observable<any[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
    const req = this.http.get<any[]>(`${this.base}/Departments`, { params: { CompanyId: companyId } });
    return req.pipe(catchError(error => throwError(() => error)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
  getStores(companyId: number): Observable<any[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
    const req = this.http.get<any[]>(`${this.base}/Stores`, { params: { CompanyId: companyId } });
    return req.pipe(catchError(error => throwError(() => error)));
  }

  // ─── Presorts ─────────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
  getPresortStats(storeId: number): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
    const req = this.http.get<any>(`${this.base}/Presorts/Stats`, { params: { StoreId: storeId } });
    return req.pipe(catchError(error => throwError(() => error)));
  }

  getPresortQueue(params: PresortQueueParams): Observable<PaginatedResult<PresortQueueResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<PresortQueueResponse>>(`${this.base}/Presorts/Queue`, { params: p });
  }

  changePresortStatus(donationId: number, cmd: ChangePresortStatusRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Presorts/Queue/${donationId}/ChangeStatus`, cmd);
  }

  // ─── Donors ───────────────────────────────────────────────────────────────

  getDonors(params: DonorsListParams): Observable<PaginatedResult<DonorResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<DonorResponse>>(`${this.base}/Donors`, { params: p });
  }

  getDonor(id: number): Observable<DonorResponse> {
    return this.http.get<DonorResponse>(`${this.base}/Donors/${id}`);
  }

  createDonor(cmd: CreateDonorRequest): Observable<DonorResponse> {
    return this.http.post<DonorResponse>(`${this.base}/Donors`, cmd);
  }

  updateDonor(id: number, cmd: UpdateDonorRequest): Observable<DonorResponse> {
    return this.http.put<DonorResponse>(`${this.base}/Donors/${id}`, cmd);
  }

  deleteDonor(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.base}/Donors/${id}`);
  }

  getDonorLoyalty(id: number): Observable<DonorLoyaltyResponse> {
    return this.http.get<DonorLoyaltyResponse>(`${this.base}/Donors/${id}/Loyalty`);
  }

  // ─── Donations ────────────────────────────────────────────────────────────

  getDonations(params: DonationsListParams): Observable<PaginatedResult<DonationResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<DonationResponse>>(`${this.base}/Donations`, { params: p });
  }

  getDonation(id: number): Observable<DonationResponse> {
    return this.http.get<DonationResponse>(`${this.base}/Donations/${id}`);
  }

  createDonation(cmd: CreateDonationRequest): Observable<DonationResponse> {
    return this.http.post<DonationResponse>(`${this.base}/Donations`, cmd);
  }

  updateDonation(id: number, cmd: UpdateDonationRequest): Observable<DonationResponse> {
    return this.http.put<DonationResponse>(`${this.base}/Donations/${id}`, cmd);
  }

  changeDonationStatus(id: number, cmd: ChangeDonationStatusRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Donations/${id}/ChangeStatus`, cmd);
  }

  associateDonation(id: number, cmd: AssociateDonationRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Donations/${id}/Associate`, cmd);
  }

  issueDonationRefund(id: number, cmd: IssueDonationRefundRequest): Observable<DonationRefundResponse> {
    return this.http.post<DonationRefundResponse>(`${this.base}/Donations/${id}/Refund`, cmd);
  }

  getDonationReceiptPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.base}/Donors/Me/Donations/${id}/receipt`, { responseType: 'blob' });
  }

  sendDonationReceiptEmail(id: number, formData: FormData): Observable<void> {
    return this.http.post<void>(`${this.base}/Donors/Me/Donations/${id}/SendReceipt`, formData);
  }

  uploadReceiptBlob(
    donationId: number,
    companyId: number,
    blob: Blob
  ): Observable<{ isSuccess: boolean; uploadedFiles: { blobFileId: number; url: string }[] }> {
    const params = new HttpParams().set('path', 'Receipt').set('companyId', String(companyId));
    const formData = new FormData();
    formData.append('file', blob, `receipt-${donationId}.pdf`);
    return this.http.post<{ isSuccess: boolean; uploadedFiles: { blobFileId: number; url: string }[] }>(
      `${this.base}/BlobFiles/Upload`,
      formData,
      { params }
    );
  }

  updateDonationReceipt(receiptId: number, blobFileId: number): Observable<void> {
    return this.http.put<void>(`${this.base}/Donations/${receiptId}/UpdateReceipt`, { blobFileId });
  }

  // ─── Containers ───────────────────────────────────────────────────────────

  getContainers(params: ContainersListParams): Observable<PaginatedResult<ContainerResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<ContainerResponse>>(`${this.base}/Containers`, { params: p });
  }

  getContainer(id: number): Observable<ContainerResponse> {
    return this.http.get<ContainerResponse>(`${this.base}/Containers/${id}`);
  }

  createContainer(cmd: CreateContainerRequest): Observable<ContainerResponse> {
    return this.http.post<ContainerResponse>(`${this.base}/Containers`, cmd);
  }

  updateContainer(id: number, cmd: UpdateContainerRequest): Observable<ContainerResponse> {
    return this.http.put<ContainerResponse>(`${this.base}/Containers/${id}`, cmd);
  }

  changeContainerStatus(id: number, cmd: ChangeContainerStatusRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Containers/${id}/ChangeStatus`, cmd);
  }

  bulkUpdateContainerStatus(cmd: { containerIds: number[]; status: number }): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.base}/Containers/BulkChangeStatus`, cmd)
      .pipe(catchError(error => throwError(() => error)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
  splitContainer(containerId: number, cmd: any): Observable<any[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped API endpoint, shape mapped by callers
    const req = this.http.post<any[]>(`${this.base}/Containers/${containerId}/Split`, cmd);
    return req.pipe(catchError(error => throwError(() => error)));
  }

  // ─── Configs ──────────────────────────────────────────────────────────────

  getLoyaltyTiers(companyId: number): Observable<LoyaltyTierConfigResponse[]> {
    const p = new HttpParams().set('companyId', companyId);
    return this.http.get<LoyaltyTierConfigResponse[]>(`${this.base}/Configs/LoyaltyTiers`, { params: p });
  }

  updateLoyaltyTier(tier: DonorTier, cmd: UpdateLoyaltyTierConfigRequest): Observable<LoyaltyTierConfigResponse> {
    return this.http.put<LoyaltyTierConfigResponse>(`${this.base}/Configs/LoyaltyTiers/${tier}`, cmd);
  }

  createStoreConfig(cmd: CreateAppConfigRequest): Observable<AppConfigResponse> {
    return this.http.post<AppConfigResponse>(`${this.base}/Configs/AppConfig`, cmd);
  }

  createLoyaltyTiersConfig(cmd: CreateLoyaltyTierRequest): Observable<LoyaltyTierConfigResponse[]> {
    return this.http.post<LoyaltyTierConfigResponse[]>(`${this.base}/Configs/LoyaltyTiers`, cmd);
  }

  updateLoyaltyTiersConfig(cmd: CreateLoyaltyTierRequest): Observable<LoyaltyTierConfigResponse[]> {
    return this.http.put<LoyaltyTierConfigResponse[]>(`${this.base}/Configs/LoyaltyTiers`, cmd);
  }

  // ─── Campaigns ────────────────────────────────────────────────────────────

  getCampaigns(params: CampaignsListParams): Observable<PaginatedResult<CampaignResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<CampaignResponse>>(`${this.base}/Campaigns`, { params: p });
  }

  getCampaign(id: number): Observable<CampaignResponse> {
    return this.http.get<CampaignResponse>(`${this.base}/Campaigns/${id}`);
  }

  createCampaign(cmd: CreateCampaignRequest): Observable<CampaignResponse> {
    return this.http.post<CampaignResponse>(`${this.base}/Campaigns`, cmd);
  }

  updateCampaign(id: number, cmd: UpdateCampaignRequest): Observable<CampaignResponse> {
    return this.http.put<CampaignResponse>(`${this.base}/Campaigns/${id}`, cmd);
  }

  changeCampaignStatus(id: number, cmd: ChangeCampaignStatusRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.base}/Campaigns/${id}/ChangeStatus`, cmd);
  }

  // ─── Configs ──────────────────────────────────────────────────────────────

  validateConfig(params: { StoreId: number; CompanyId: number }): Observable<ConfigValidationResponse> {
    const p = this.toParams(params);
    return this.http.get<ConfigValidationResponse>(`${this.base}/Configs/Validate`, { params: p });
  }

  // ─── Payment Terminals ────────────────────────────────────────────────────

  getPaymentTerminals(params: PaymentTerminalListParams): Observable<PaginatedResult<ApiPaymentTerminalResponse>> {
    const p = this.toParams(params);
    return this.http.get<PaginatedResult<ApiPaymentTerminalResponse>>(`${this.base}/PaymentTerminals`, { params: p });
  }

  getPaymentTerminalById(id: number): Observable<ApiPaymentTerminalResponse> {
    return this.http.get<ApiPaymentTerminalResponse>(`${this.base}/PaymentTerminals/${id}`);
  }

  createPaymentTerminal(cmd: CreatePaymentTerminalRequest): Observable<ApiPaymentTerminalResponse> {
    return this.http.post<ApiPaymentTerminalResponse>(`${this.base}/PaymentTerminals`, cmd);
  }

  updatePaymentTerminal(id: number, cmd: UpdatePaymentTerminalRequest): Observable<ApiPaymentTerminalResponse> {
    return this.http.put<ApiPaymentTerminalResponse>(`${this.base}/PaymentTerminals/${id}`, cmd);
  }

  changeTerminalStatus(id: number, status: number): Observable<void> {
    return this.http.post<void>(`${this.base}/PaymentTerminals/${id}/ChangeStatus`, { transitionTo: status });
  }

  deletePaymentTerminal(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/PaymentTerminals/${id}`);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts heterogeneous param objects
  private toParams(obj: Record<string, any>): HttpParams {
    let p = new HttpParams();
    for (const [key, val] of Object.entries(obj)) {
      if (val !== undefined && val !== null && val !== '') {
        p = p.set(key, String(val));
      }
    }
    return p;
  }
}
